import React from "react";
import { BookOpen, Zap, Shield, Code2, Layers } from "lucide-react";
import { agentCompetencies } from "@/lib/agent-competencies";
import CompetencyCard from "@/components/CompetencyCard";

const Documentation: React.FC = () => {
  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <div className="flex justify-center mb-6">
              <div className="bg-orange-500 rounded-full p-4">
                <BookOpen className="h-8 w-8 text-white" />
              </div>
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold mb-4">
              Documentation
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Technical documentation and API reference for infrastructure
              agents
            </p>
          </div>

          {/* What are Infrastructure Agents */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold mb-6 flex items-center">
              <Zap className="h-8 w-8 text-orange-500 mr-3" />
              What are Infrastructure Agents?
            </h2>
            <div className="bg-white dark:bg-slate-800 rounded-xl p-8 border border-slate-200 dark:border-slate-700">
              <p className="text-lg leading-relaxed text-slate-700 dark:text-slate-300 mb-4">
                <strong className="text-orange-600 dark:text-orange-400">
                  Infrastructure Agents
                </strong>{" "}
                are autonomous or semi-autonomous software entities that bridge
                human intent and machine execution to manage cloud and
                on-premises infrastructure. Leveraging artificial intelligence,
                machine learning, and advanced reasoning, they interpret
                high-level business goals or declarative configurations,
                dynamically generate and execute infrastructure code (e.g.,
                IaC), and continuously monitor system state to perform real-time
                remediation and optimization.
              </p>
              <p className="text-lg leading-relaxed text-slate-700 dark:text-slate-300">
                Beyond operational automation, they coordinate multi-agent
                collaboration, abstract away the complexity of diverse APIs and
                tooling, and embed governance to enforce security, compliance,
                and organizational policies. By aligning infrastructure behavior
                with strategic objectives, Infrastructure Agents evolve
                traditional automation into a self-optimizing, intelligent
                paradigm that ensures efficiency, reliability, and adaptability
                at scale.
              </p>
            </div>
          </section>

          {/* Getting Started */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold mb-6 flex items-center">
              <Code2 className="h-8 w-8 text-orange-500 mr-3" />
              Getting Started
            </h2>
            <div className="bg-white dark:bg-slate-800 rounded-xl p-6 border border-slate-200 dark:border-slate-700">
              <h3 className="text-xl font-semibold mb-4 text-slate-900 dark:text-slate-100">
                Quick Start Guide
              </h3>
              <div className="space-y-4 text-slate-700 dark:text-slate-300">
                <div className="flex items-start">
                  <span className="text-orange-500 mr-3 mt-1">1.</span>
                  <div>
                    <strong>Browse Agents:</strong> Explore our curated
                    collection of infrastructure agents on the home page.
                  </div>
                </div>
                <div className="flex items-start">
                  <span className="text-orange-500 mr-3 mt-1">2.</span>
                  <div>
                    <strong>Filter by Technology:</strong> Use tags to find
                    agents that match your tech stack (AWS, Kubernetes,
                    Terraform, etc.).
                  </div>
                </div>
                <div className="flex items-start">
                  <span className="text-orange-500 mr-3 mt-1">3.</span>
                  <div>
                    <strong>Review Documentation:</strong> Each agent includes
                    detailed documentation, source code, and examples.
                  </div>
                </div>
                <div className="flex items-start">
                  <span className="text-orange-500 mr-3 mt-1">4.</span>
                  <div>
                    <strong>Deploy & Test:</strong> Follow the agent's README
                    for installation and configuration instructions.
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Agent Competencies */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold mb-6 flex items-center">
              <Layers className="h-8 w-8 text-orange-500 mr-3" />
              Agent Competencies
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Contribute different modalities of infrastructure agents.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {agentCompetencies.map((competency) => (
                <CompetencyCard key={competency.id} competency={competency} />
              ))}
            </div>
          </section>

          {/* Platform Features */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold mb-6 flex items-center">
              <Shield className="h-8 w-8 text-orange-500 mr-3" />
              Platform Features
            </h2>
            <div className="bg-white dark:bg-slate-800 rounded-xl p-6 border border-slate-200 dark:border-slate-700">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-slate-700 dark:text-slate-300">
                <div>
                  <strong>🔍 Advanced Search:</strong> Find agents by name,
                  description, or technology tags
                </div>
                <div>
                  <strong>⭐ Community Ratings:</strong> See likes and views to
                  gauge popularity
                </div>
                <div>
                  <strong>🏷️ Tag Filtering:</strong> Filter by specific
                  technologies and use cases
                </div>
                <div>
                  <strong>📚 Comprehensive Docs:</strong> Each agent includes
                  detailed documentation
                </div>
                <div>
                  <strong>🔗 Source Code Access:</strong> Direct links to GitHub
                  repositories
                </div>
                <div>
                  <strong>🌐 Live Demos:</strong> Optional website links for
                  demonstrations
                </div>
              </div>
            </div>
          </section>

          {/* Call to Action */}
          <section className="text-center">
            <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl p-8 text-white">
              <h2 className="text-2xl lg:text-3xl font-bold mb-4">
                Ready to Contribute?
              </h2>
              <p className="text-lg mb-6 text-orange-100">
                Learn how to create and submit infrastructure agents following
                our guidelines
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="/guidelines"
                  className="inline-flex items-center justify-center bg-white text-orange-600 px-8 py-3 rounded-lg font-semibold hover:bg-orange-50 transition-colors"
                >
                  View Guidelines
                </a>
                <a
                  href="/submit"
                  className="inline-flex items-center justify-center border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white/10 transition-colors"
                >
                  Submit Infrastructure Agent
                </a>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Documentation;
